import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useOrganizationApiKeyStatus,
  usePersonalApiKeyStatus,
} from './api/useApiKeys';
import { useParams } from 'react-router-dom';

type UnknownTabType = Record<string, unknown>;

interface UseDynamicTabsProps<T extends UnknownTabType = UnknownTabType> {
  tabs: T;
  selectorKey: keyof T;
  organizationId?: string;
  /**
   * When the user has an API key, `selectorKey` is placed immediately after this tab.
   * If omitted, `attack-demonstrations` is used when present on `tabs`; otherwise the
   * tab is moved to the end (insertion order).
   */
  installAfterKey?: keyof T;
}

interface APIKeyStatus {
  hasKey: boolean;
  createdAt?: Date | null | undefined;
  keyPrefix?: string | null | undefined;
  lastUsedAt?: Date | null | undefined;
}

interface CacheStore {
  apiKeyStatus: APIKeyStatus | undefined;
  organizationIdParams: string | undefined;
}

const useReorderTabsCache = () => {
  const cache = useRef<CacheStore>({
    apiKeyStatus: undefined,
    organizationIdParams: undefined,
  });

  const setApiKeyStatusCache = (status: APIKeyStatus | undefined) => {
    cache.current.apiKeyStatus = status;
  };

  const setOrganizationIdParamsCache = (orgId: string | undefined) => {
    cache.current.organizationIdParams = orgId;
  };

  const getAPIKeyStatusCache = () => {
    return cache.current.apiKeyStatus;
  };

  const getOrganizationIdParamsCache = () => {
    return cache.current.organizationIdParams;
  };

  return {
    setApiKeyStatusCache,
    setOrganizationIdParamsCache,
    getAPIKeyStatusCache,
    getOrganizationIdParamsCache,
  };
};

function resolveInstallAnchorKey<T extends UnknownTabType>(
  tabs: T,
  installAfterKey: keyof T | undefined
): keyof T | null {
  if (installAfterKey !== undefined && installAfterKey in tabs) {
    return installAfterKey;
  }
  const defaultAnchor = 'attack-demonstrations' as keyof T;
  if (defaultAnchor in tabs) {
    return defaultAnchor;
  }
  return null;
}

/**
 * Moves `selectorKey` to immediately after `anchorKey`, preserving relative order of other tabs.
 */
function reorderTabAfterAnchor<T extends UnknownTabType>(
  tabs: T,
  selectorKey: keyof T,
  anchorKey: keyof T
): T {
  const tabKeys = Object.keys(tabs) as (keyof T)[];
  const anchorStr = String(anchorKey);
  const selectorStr = String(selectorKey);

  const result = {} as T;
  for (const key of tabKeys) {
    if (String(key) === selectorStr) {
      continue;
    }
    result[key] = tabs[key];
    if (String(key) === anchorStr) {
      result[selectorKey] = tabs[selectorKey];
    }
  }
  return result;
}

/** Moves `selectorKey` to the end of insertion order (legacy fallback when no anchor tab). */
function reorderTabToEnd<T extends UnknownTabType>(
  tabs: T,
  selectorKey: keyof T
): T {
  const newConfig = { ...tabs };
  const moving = newConfig[selectorKey];
  delete newConfig[selectorKey];
  newConfig[selectorKey] = moving;
  return newConfig;
}

export const useReorderTabsOnSetup = <
  T extends UnknownTabType = UnknownTabType,
>({
  tabs,
  selectorKey,
  organizationId,
  installAfterKey,
}: UseDynamicTabsProps<T>) => {
  const {
    data: personalApiKeyStatus,
    isLoading: isLoadingPersonalKey,
    isError: isErrorPersonalKey,
  } = usePersonalApiKeyStatus({ enabled: !organizationId });
  const {
    data: orgApiKeyStatus,
    isLoading: isLoadingOrgKey,
    isError: isErrorOrgKey,
  } = useOrganizationApiKeyStatus(organizationId ?? '', !!organizationId);
  const { organizationId: orgIdParam } = useParams<{
    organizationId: string;
  }>();
  const {
    getAPIKeyStatusCache,
    setOrganizationIdParamsCache,
    setApiKeyStatusCache,
    getOrganizationIdParamsCache,
  } = useReorderTabsCache();

  const isLoading = organizationId ? isLoadingOrgKey : isLoadingPersonalKey;
  const isError = organizationId ? isErrorOrgKey : isErrorPersonalKey;
  const apiKeyStatus = organizationId ? orgApiKeyStatus : personalApiKeyStatus;

  const [tabConfig, setTabConfig] = useState<Partial<T>>(tabs);

  /**
   * Reorder tabs once API key status is known: without a key, keep default order (Get Started first).
   * With a key, place the install / Get Started tab immediately after Attack Demonstrations
   * (or after `installAfterKey` / end fallback).
   */
  const initializedTabConfig = useCallback((): void => {
    const keyStatus = getAPIKeyStatusCache();
    if (isError || (keyStatus && !keyStatus?.hasKey)) {
      setTabConfig(tabs);
      return;
    }
    const userSetup = keyStatus?.hasKey;
    if (userSetup) {
      const anchor = resolveInstallAnchorKey(tabs, installAfterKey);
      const newConfig =
        anchor !== null
          ? reorderTabAfterAnchor(tabs, selectorKey, anchor)
          : reorderTabToEnd(tabs, selectorKey);
      setTabConfig(newConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installAfterKey, setTabConfig, selectorKey, isError, tabs]);

  /**
   * Sync API key status into the ref cache, then apply tab order in the same tick so
   * `initializedTabConfig` never reads a stale cache (see cache vs. tab-order effect ordering).
   *
   * Also resets the cache when `organizationId` route param changes so org switching
   * recalculates tab order.
   */
  useEffect(() => {
    const apiKeyStatusCache = getAPIKeyStatusCache();
    if (apiKeyStatusCache === undefined) {
      setApiKeyStatusCache(apiKeyStatus);
    }

    const orgIdChanged = getOrganizationIdParamsCache() !== orgIdParam;
    if (orgIdChanged) {
      setOrganizationIdParamsCache(orgIdParam);
      setApiKeyStatusCache(apiKeyStatus);
    }

    initializedTabConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKeyStatus, orgIdParam, initializedTabConfig, isLoading, isError]);

  return {
    tabConfig,
    isLoading,
    isSetup: Boolean(apiKeyStatus?.hasKey),
  };
};
